import React, { useState, useEffect, useRef } from 'react'
import { Box, Button } from '@mui/material'

import '../../styles/css/viewer.css'
import { ImageData, FullNodeProp, ColorLibObj } from '../../utils/types'
import { get, post } from '../../utils/http'
import Viewer from './compareImage/viewer'
import Viewer2 from './compareImage/viewer_2'
import { outputS3 } from '../../utils/config'
import { MapWarningTexts } from './helper/uiElements'

interface Props {
    slide1: FullNodeProp | null,
    slide2: FullNodeProp | null,
    duplicateSlides: Function
}

const CompareImageViewer = ({ slide1, slide2, duplicateSlides }: Props) => {
    const cacheRef = useRef<Map<string, string[]>>(new Map())

    const [colorLib, setColorLib] = useState<ColorLibObj[]>([])
    const [image1, setImage1] = useState<ImageData | null>(null)
    const [image2, setImage2] = useState<ImageData | null>(null)
    const [outputFiles1, setOutputFiles1] = useState<string[]>([])
    const [outputFiles2, setOutputFiles2] = useState<string[]>([])

    const pubOutputS3Bucket: string = outputS3 || ''

    const getPredictionFileList = async (imageData: ImageData, forceUpdate: boolean = false) => {
        const key = imageData.image_unique_id
        if (cacheRef.current.has(key) && !forceUpdate) return cacheRef.current.get(key)!
        try {
            const files = await post(`S3/filecontents`, { directory : imageData.image_unique_id, s3_name: pubOutputS3Bucket })
            if (imageData.output_s3) {
                const s3Path = imageData.output_s3 ? imageData.output_s3.split('/').slice(1).join('/') : ''
                const slideName = imageData.name.split('/').pop()!
                const temp = await post(`S3/filecontents`, { directory : s3Path.length ? `${s3Path}/${slideName}` : imageData.name, s3_name: imageData.output_s3.split('/')[0] })
                files.push(...temp)
            }
            cacheRef.current.set(key, files)
            return files
        } catch (err) {
            return []
        }
    }

    const fetchImageData1 = async (slide: FullNodeProp) => {
        try {
            setImage1(null)
            const res = await get(`Images/string-id/${slide.file_id}`)
            setTimeout(() => setImage1(res), 0)
        } catch (err) {
            setImage1(null)
        }
    }

    const getColors = async () => {
        const res = await get('Annotationlibs')
        setColorLib(res)
    }

    useEffect(() => {
        getColors()
    }, [])

    useEffect(() => {
        if (slide1) fetchImageData1(slide1)
    }, [slide1])

    const fetchImageData2 = async (slide: FullNodeProp) => {
        try {
            setImage2(null)
            const res = await get(`Images/string-id/${slide.file_id}`)
            setTimeout(() => setImage2(res), 0)
        } catch (err) {
            setImage2(null)
        }
    }

    useEffect(() => {
        if (slide2) fetchImageData2(slide2)
    }, [slide2])

    useEffect(() => {
        if (image1) {
            getPredictionFileList(image1).then(res => setOutputFiles1(res))
        }
    }, [image1])  // eslint-disable-line

    useEffect(() => {
        if (image2) {
            getPredictionFileList(image2).then(res => setOutputFiles2(res))
        }
    }, [image2])  // eslint-disable-line

    const refreshXmlList1 = async () => {
        const res = await getPredictionFileList(image1!, true)
        setOutputFiles1(res)
    }
    const refreshXmlList2 = async () => {
        const res = await getPredictionFileList(image2!, true)
        setOutputFiles2(res)
    }

    return(
        <Box sx={{ display: 'flex', width: '100%', gap: '0.25rem' }} className="map-height">
            {image1
                ? <Viewer
                    image={image1}
                    colorLib={colorLib}
                    outputFiles={outputFiles1}
                    refreshXmlList={refreshXmlList1}
                />
                : slide1
                    ? <MapWarningTexts text={"..."} loader={true} />
                    : <MapWarningTexts text={"Slide 1"} />
            }
            {image2
                ? <Viewer2
                    image={image2}
                    colorLib={colorLib}
                    outputFiles={outputFiles2}
                    refreshXmlList={refreshXmlList2}
                />
                : image1
                    ? <Button className='w-50' onClick={() => duplicateSlides()}>Load Slide 1</Button>
                    : slide2
                        ? <MapWarningTexts text={"..."} loader={true} />
                        : <MapWarningTexts text={"Slide 2"} />
            }
        </Box>
    )
}

export default CompareImageViewer